import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useStore } from 'store/useStore'
import { ActivateELockForm } from 'sections/LKSection/modules/ELockModule/components/ActivateELockForm/ActivateELockForm'
import { OrderELockForm } from 'sections/LKSection/modules/ELockModule/components/OrderELockForm/OrderELockForm'
import { ElockInfoStatus, ElockReason } from 'sections/LKSection/modules/ELockModule/types'
import { PickPointFilesForm } from 'sections/LKSection/shared/PickPointSharedModule/components/PickPointFilesForm/PickPointFilesForm'
import { PickPointType } from 'sections/LKSection/shared/PickPointSharedModule/types'
import { Margin } from 'components/ui'
import { Button, Divider, Flex, Form, FormItem, Text, TextArea, Title } from 'components/uiKit'
import { LKRoutesEnum } from 'shared/constants/routes'
import { DATE_FORMAT_WITH_TIME } from 'shared/constants/values'
import { Country } from 'shared/types/types'

import { OrderLocationForm } from '../../../components/Forms/OrderLocationForm/OrderLocationForm'
import { OrderWorkConditionsForm } from '../../../components/Forms/OrderWorkConditionsForm/OrderWorkConditionsForm'
import { Subsidy } from '../../../components/Subsidy/Subsidy'
import { Tariff } from '../../../components/Tariff/Tariff'
import { countriesWithSubsidy, OrderKeys, orderStatusTranslator } from '../../../data'
import { Order } from '../../../stores/entity/Order'
import { OrderStatus } from '../../../types'
import { getActivateOrderPageKey, getOrderPageKey } from '../../../utils'
import { OrderComments } from './OrderComments/OrderComments'
import { validateOrderUpdate } from './validate'

type OrderItemFormProps = {
  order: Order
}

export const OrderItemForm = observer(({ order }: OrderItemFormProps) => {
  const { viewOrderUiStore: uiStore, createOrderUiStore, appStore, eLockUiStore } = useStore()

  const navigate = useNavigate()
  const { t } = useTranslation()

  const [loading, setLoading] = useState(false)

  const canEditOrder =
    (order.status === OrderStatus.draft || order.status === OrderStatus.remarkFromCreated) && !order.deleted
  const isActivation =
    order.status === OrderStatus.readyActivate ||
    order.status === OrderStatus.remarkFromActivate ||
    order.status === OrderStatus.pickPointCreated
  const isMoving = Boolean(uiStore.selectedOrder?.exPickPointId)

  const updateButtonHandler = async () => {
    if (
      validateOrderUpdate.validate(
        uiStore.location,
        uiStore.workConditions,
        uiStore.fileList,
        appStore.companyCountry,
        isMoving,
      )
    ) {
      setLoading(true)

      if (await uiStore.updateOrder()) {
        navigate(LKRoutesEnum.orders)
      }

      setLoading(false)
    }
  }

  const getKey = (key: string) => `${OrderKeys.orderItemPage}.${key}`

  return (
    <Flex vertical>
      <Flex vertical gap={8}>
        <Title level={5}>{t(getKey('Общая информация'))}</Title>

        <Flex vertical>
          <Text strong>{t(getKey('Заявка создана'))}:</Text>
          <Text>{dayjs(order.created).format(DATE_FORMAT_WITH_TIME)}</Text>
        </Flex>

        <Flex vertical>
          <Text strong>{t(getKey('Статус заявки'))}:</Text>
          <Text>{t(getOrderPageKey(orderStatusTranslator[order.status]?.title))}</Text>
        </Flex>
      </Flex>

      <Divider />

      <Margin mt={8}>
        <OrderWorkConditionsForm
          uiStore={uiStore.workConditions}
          disabled={!canEditOrder}
          pickPointType={uiStore.location.pickPointType ?? PickPointType.partner}
          floorLevel={uiStore.location.floorLevel}
        />
      </Margin>

      <Divider />

      <OrderLocationForm
        uiStore={uiStore.location}
        disabled={!canEditOrder}
        country={uiStore.selectedOrder?.country ?? Country.RU}
        isMoving={isMoving}
      />

      <Tariff
        area={order.area}
        latitude={+order.latitude}
        longitude={+order.longitude}
        status={order.status}
        tariffDescription={order.tariffDescription}
        typePoint={order.typePoint}
      />

      {uiStore.selectedOrder?.country && countriesWithSubsidy.includes(uiStore.selectedOrder.country) && (
        <Margin mt={8}>
          <Subsidy
            tariff={createOrderUiStore.tariff}
            country={uiStore.selectedOrder?.country ?? Country.RU}
            location={uiStore.location}
          />
        </Margin>
      )}

      <Divider />

      <Margin mt={8}>
        <PickPointFilesForm
          uiStore={uiStore.fileList}
          entity={order}
          disabled={!canEditOrder}
          isActivation={isActivation}
          initialized={uiStore.initialized}
        />
      </Margin>

      {order.eLockInfo &&
        order.eLockInfo.status !== ElockInfoStatus.none &&
        (order.eLockInfo?.reason === ElockReason.none ? (
          <ActivateELockForm fromOrder />
        ) : (
          <>
            <OrderELockForm uiStore={eLockUiStore.eLockReason} disabled />
            <Divider />
          </>
        ))}

      <Margin my={16}>{order && <OrderComments order={order} />}</Margin>

      {canEditOrder && (
        <>
          <Form>
            <FormItem label={t(getActivateOrderPageKey('Сообщение оператору'))}>
              <TextArea
                value={uiStore.comment}
                onChange={(e) => uiStore.setComment(e.target.value)}
                maxLength={500}
                placeholder={t(getActivateOrderPageKey('При необходимости опишите'))}
              />
            </FormItem>
          </Form>

          <Button htmlType="submit" disabled={uiStore.isNoChanges} loading={loading} onClick={updateButtonHandler}>
            {t(getKey('Обновить заявку'))}
          </Button>
        </>
      )}
    </Flex>
  )
})
